// React
import { useCallback, useState, useRef, useEffect } from 'react'
// Nextjs
import { useRouter } from 'next/router'
import Link from 'next/link'
import {
  Flex, Heading,
  List,
  ListItem,
  Box,
  InputGroup,
  InputLeftElement,
  Input,
  Show,
  Button
} from '@chakra-ui/react'
import Select from 'react-select'
import _ from "lodash";
// Redux
import { useSelector, useDispatch } from 'react-redux'
import { selectSite, addSearchGame, addActiveVendor } from '../store/slice/site'
// Icon
import SearchIcon from '../components/icons/Search'
import CheckIcon from '../components/icons/Check'
// Css
import styles from '../styles/Menu.module.scss'

const Menu = ({ searchGame }) => {
  const dispatch = useDispatch()
  const state = useSelector(selectSite)
  const router = useRouter()
  const listInnerRef = useRef()
  // const [temp, setTemp] = useState() // 暫時的關鍵字
  const [search, setSearch] = useState() // 搜尋關鍵字
  const [reachedBottom, setReachedBottom] = useState(false)
  const [options, setOptions] = useState([]) // H5 產品商下拉選單選項
  const [selectValue, setSelectValue] = useState({}) // H5 產品商下拉選單 Value

  /*--- Menu 遮罩漸層色 ---*/
  const linearColor = () => {
    return state.theme === 'light'
      ? 'linear-gradient(360deg, #FFFFFF 28.6%, rgba(255, 255, 255, 0) 100%);'
      : 'linear-gradient(360deg, #303030 28.6%, rgba(48, 48, 48, 0) 99.48%);'
  }

  // 監聽 scroll 事件
  const onScroll = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef?.current

      // 抵達底部
      if (scrollTop + clientHeight === scrollHeight) {
        setReachedBottom(true)
      } else {
        setReachedBottom(false)
      }
    }
  }

  /*--- H5 Select 下拉選單樣式 ---*/
  const selectStyle = {
    option: (styles, { isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isSelected
          ? '#104B83'
          : isFocused
            ? '#104B83'
            : undefined,
        color: isSelected
          ? '#fff'
          : isFocused
            ? '#fff'
            : undefined
      };
    },
    container: style => ({ ...style, width: '100%' }),
    menu: style => ({ ...style, zIndex: 995 }),
    menuList: style => ({
      ...style,
      "::-webkit-scrollbar": {
        width: "4px",
        height: "0px",
      },
      "::-webkit-scrollbar-track": {
        background: "#f1f1f1"
      },
      "::-webkit-scrollbar-thumb": {
        background: "#104b83c2"
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#104B83"
      }
    }),
    control: style => ({ ...style, borderRadius: '50px', borderColor: '#808080', minHeight: '40px' }),
    singleValue: style => ({ ...style, color: '#104B83', fontWeight: '600' }),
    indicatorSeparator: style => ({ ...style, backgroundColor: 'none' }),
    indicatorsContainer: style => ({ ...style, color: '#104B83' })
  }

  // 切換 Vendor
  const switchVendor = (vendorCode) => {
    const activeVendor = state.vendors.filter(vendor => vendor.code === vendorCode)

    // 更新 Redux 目前的 Vendor
    dispatch(addActiveVendor(activeVendor[0]))

    // 加上 scroll: false，換頁時才不會回到 scroll to top
    router.push({
      pathname: `/${state.type}/${vendorCode}`
    }, undefined, { scroll: false })
  }

  /*--- H5 Select 下拉選單語系切換 ---*/
  useEffect(() => {
    if (state?.vendors?.length > 0) {
      const options = state.vendors.map(item => (
        {
          value: item.code,
          label: state.language === 'zh' ? item.name_zh : item.name_en
        }
      ))
      setOptions(options)
    }

  }, [state.language, state.vendors])

  /*--- 搜尋遊戲 ----*/
  const handleSearch = () => {
    dispatch(addSearchGame(search))
    searchGame()
  }

  /*--- 切換路由時清空搜尋內容 ---*/
  useEffect(() => {
    setSearch('')
    setSelectValue(options.filter(option => option.value === router?.query?.vendor)[0])
    // setSelectValue()
    dispatch(addSearchGame(''))
  }, [dispatch, router.query.type, router.query.vendor])


  return (
    <>
      {/* web 側邊欄 */}
      <Show above='lg'>
        <Flex
          direction="column"
        >
          <Heading size="lg" color="#104B83">
            Partners
          </Heading>
          <InputGroup
            mt='0.5rem' mb='1.5rem'
            display='flex'
            alignItems={'center'}
            borderBottom={reachedBottom ? '1px solid #B7B7B7' : 'none'}
          >
            <InputLeftElement
              pointerEvents='none'
              // eslint-disable-next-line react/no-children-prop
              children={<SearchIcon />}
            />
            <Input
              variant='flushed'
              placeholder='Search'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <Button
              type='button'
              bg={'linear-gradient(90deg, #104B83 30.91%, #0088B3 100%)'}
              borderRadius={'30px'}
              w={'30px'}
              h={'30px'}
              color={'white'}
              p={'4px'}
              fontSize={'14px'}
              onClick={() => handleSearch()}
            >
              GO
            </Button>
          </InputGroup>

          <List
            className={styles['menu-scroll-bar']}
            height={'auto'}
            maxHeight={'50vw'}
            overflowY={{ md: 'scroll' }}
            borderBottom={'1px solid #B7B7B7'}
            onScroll={() => onScroll()}
            ref={listInnerRef}
          >
            {
              state?.vendors?.length > 0 && state?.vendors?.map((item, i) => {
                return (
                  <ListItem key={`list-${item.code}`} mb="1.5rem">
                    <Flex minWidth='max-content' alignItems='center' justifyContent="space-between" pr='0.5rem'>
                      <Box
                        as='a'
                        color={router.query.vendor === item.code ? '#104B83' : '#808080'}
                        fontWeight='600'
                        cursor={'pointer'}
                        _hover={{
                          color: '#104B83'
                        }}
                        onClick={() => switchVendor(item.code)}
                      >
                        {state?.language && state.language === 'zh'
                          ? item.name_zh
                          : item.name_en}
                      </Box>
                      <CheckIcon
                        boxSize='1.1rem'
                        color='#104B83'
                        display={router.query.vendor === item.code ? 'block' : 'none'} />
                    </Flex>
                  </ListItem>
                )
              })
            }
          </List>

          {/* 產品商數量大於 12個才顯示遮罩 */}
          {state?.vendors?.length > 12 && (
            <Box
              w={'100%'}
              height={'250px'}
              pos={'relative'}
              top={'-9rem'}
              bg={linearColor()}
              zIndex={3}
              opacity={reachedBottom ? 0 : 1}
              transition={'all .35s ease'}
              pointerEvents='none'
            />
          )}
        </Flex>
      </Show>

      {/* H5 側邊欄 */}
      <Show below='lg'>
        <Flex align='center' direction='column'>
          <Heading size="lg" color="#104B83" mb='1.3rem'>
            Partners
          </Heading>
          <Flex
            gap='1.3rem'
            alignItems={'center'}
            flexDir={{ base: 'column', mmd: 'row' }}
          >
            <Box width={{ base: '100%', mmd: '50%' }} className={styles['menu-scroll-bar']}>
              <Select
                width='100%'
                value={selectValue}
                options={options}
                styles={selectStyle}
                isSearchable={false}
                placeholder={'Select Partners'}
                onChange={(e) => {
                  setSelectValue(e)
                  switchVendor(e.value)
                }}
              />
            </Box>
            <Flex
              flexDir='row'
              alignItems={'center'}
              justifyContent={'space-round'}
            >
              <InputGroup>
                <InputLeftElement
                  pointerEvents='none'
                  // eslint-disable-next-line react/no-children-prop
                  children={<SearchIcon />}
                  zIndex={1}
                />
                <Input
                  placeholder='Search'
                  borderRadius='50px'
                  borderColor='#808080'
                  value={search}
                  onChange={e => {
                    setSearch(e.target.value)
                  }}
                />
              </InputGroup>
              <Button
                type='button'
                bg={'linear-gradient(90deg, #104B83 30.91%, #0088B3 100%)'}
                borderRadius={'30px'}
                w={'30px'}
                h={'30px'}
                ml={{ base: '10px' }}
                color={'white'}
                p={'4px'}
                fontSize={'14px'}
                onClick={() => handleSearch()}
              >
                GO
              </Button>
            </Flex>
          </Flex>

        </Flex>

      </Show>
    </>
  )
}

export default Menu;